/**
 * Magic Cut — extends the auto-captions cleaner from "drop fillers from the
 * caption text" to "ALSO physically cut those moments out of the burned
 * video + its audio track".
 *
 * Problem this fixes:
 *   Today our cleaner (auto-captions-cleaner.js) strips fillers + long
 *   silences from the caption text, but the burned MP4 still has every "um"
 *   audible and every long pause intact. Users hit "Remove fillers" expecting
 *   their video to get shorter — Submagic / VEED both do this and charge
 *   $20-30/mo for it (Submagic calls it "Magic Cut", VEED "Magic Wand").
 *
 * Public API (window.__acMagicCut):
 *   getCutListFromCleaner()
 *     → [{start, end}, …]
 *     Reads the LAST cleaner-run `removedRanges` cached by the pipeline plus
 *     any ranges the user manually deleted via edit-by-transcript (when that
 *     sibling feature lands), merges them, returns sorted non-overlapping
 *     ranges on the ORIGINAL-timeline.
 *
 *   isActive()
 *     → bool
 *     True when (a) the "Also remove these moments from video" checkbox is
 *     checked AND (b) we have captions AND (c) cuts exist.
 *
 *   previewSecondsCut()
 *     → number (seconds, 1 dp)
 *     Total span covered by the cut list. Used by the UI chip
 *     "-12.4 s from video" so the user sees the impact before hitting burn.
 *
 *   buildFFmpegSelectFilter(durationSec)
 *     → { vf, af } or null
 *     Returns a filter_complex-friendly select / aselect pair that DROPS the
 *     cut ranges and re-stamps PTS so the output is continuous. null when
 *     no cuts. The sibling __acEditByTranscript module will expose the same
 *     helper if/when it lands; we keep a local copy so this feature ships
 *     standalone and doesn't depend on parallel work.
 *
 * Wiring:
 *   - pipeline.js caches every cleaner result on `state.lastCleanerRun` so
 *     getCutListFromCleaner() can read it without re-running the cleaner.
 *   - blade adds checkbox `#ac-magic-cut-video` under `#ac-clean-silences`
 *     and a chip `#ac-magic-cut-preview` that mirrors previewSecondsCut().
 *   - burn pipeline (auto-captions-pipeline.js __acBurnExport) calls
 *     __acMagicCut.getCutListFromCleaner() + buildFFmpegSelectFilter() and
 *     splices the filter into exporter.plan() with audioCopy=false.
 *
 * Constraint: when cuts are active, `audioCopy` MUST flip to false because
 * `aselect` re-encodes audio. The standard burn path uses `audioCopy:true`
 * by default — we override.
 */
(function(){
    'use strict';

    var STORAGE_KEY = 'ac_magic_cut_v1';

    /* Read the user-deleted ranges that the parallel edit-by-transcript
       module exposes (when it lands). We probe the global lazily so this
       module ships standalone — magic cut works even without that sibling. */
    function readUserDeletedRanges() {
        try {
            var ebt = window.__acEditByTranscript;
            if (!ebt) return [];
            if (typeof ebt.getDeletedRanges === 'function') {
                var r = ebt.getDeletedRanges();
                return Array.isArray(r) ? r.map(toRange).filter(Boolean) : [];
            }
            return [];
        } catch (e) {
            return [];
        }
    }

    /* Normalise inputs from any of: [a,b]  |  {start,end}  →  {start,end}
       and reject zero / negative-length ranges so they don't pollute the
       cut list. */
    function toRange(r) {
        var a, b;
        if (Array.isArray(r)) { a = r[0]; b = r[1]; }
        else if (r && typeof r === 'object') { a = r.start; b = r.end; }
        else return null;
        a = +a; b = +b;
        if (!isFinite(a) || !isFinite(b)) return null;
        if (b <= a) return null;
        return { start: a, end: b };
    }

    /* Merge sorted, possibly-overlapping ranges. Same algorithm as the
       cleaner's mergeRanges but tolerant of {start,end} objects. */
    function mergeRanges(ranges) {
        if (!ranges || !ranges.length) return [];
        var sorted = ranges.slice().sort(function(x, y){ return x.start - y.start; });
        var out = [{ start: sorted[0].start, end: sorted[0].end }];
        for (var i = 1; i < sorted.length; i++) {
            var cur = sorted[i];
            var last = out[out.length - 1];
            if (cur.start <= last.end + 0.02) {
                if (cur.end > last.end) last.end = cur.end;
            } else {
                out.push({ start: cur.start, end: cur.end });
            }
        }
        return out;
    }

    function getCutListFromCleaner() {
        var state = window.__acState;
        var fromCleaner = [];
        if (state && state.lastCleanerRun && Array.isArray(state.lastCleanerRun.removedRanges)) {
            for (var i = 0; i < state.lastCleanerRun.removedRanges.length; i++) {
                var rr = toRange(state.lastCleanerRun.removedRanges[i]);
                if (rr) fromCleaner.push(rr);
            }
        }
        var fromUser = readUserDeletedRanges();
        var merged = mergeRanges(fromCleaner.concat(fromUser));
        return merged;
    }

    function isActive() {
        var checkbox = document.getElementById('ac-magic-cut-video');
        if (!checkbox || !checkbox.checked) return false;
        var state = window.__acState;
        if (!state || !state.words || !state.words.length) return false;
        // Cleanup toggles must have run with at least one of the two
        // ranges-producing rules enabled; otherwise removedRanges is empty.
        var fillers = document.getElementById('ac-clean-fillers');
        var silences = document.getElementById('ac-clean-silences');
        var repeated = document.getElementById('ac-clean-repeated');
        var anyOn = (fillers && fillers.checked) ||
                    (silences && silences.checked) ||
                    (repeated && repeated.checked);
        if (!anyOn) return false;
        return getCutListFromCleaner().length > 0;
    }

    function previewSecondsCut() {
        var ranges = getCutListFromCleaner();
        var total = 0;
        for (var i = 0; i < ranges.length; i++) total += (ranges[i].end - ranges[i].start);
        return Math.round(total * 10) / 10;
    }

    /* Build FFmpeg `select` / `aselect` filter pair that DROPS the cut
       ranges. Inverted from the more obvious approach — building one big
       `between(t,a,b)+between(t,c,d)+…` predicate that KEEPS-non-cuts is
       cleaner than trying to express "everything except". Then setpts /
       asetpts collapse the gaps so the output is continuous.

       Returns null when no cuts so callers can early-out. */
    function buildFFmpegSelectFilter(durationSec) {
        var ranges = getCutListFromCleaner();
        if (!ranges.length) return null;
        var dur = +durationSec || 0;
        /* Build inverse keep-list: walk through `ranges` (already sorted +
           non-overlapping) and emit the gaps as keep regions. */
        var keep = [];
        var cursor = 0;
        for (var i = 0; i < ranges.length; i++) {
            var s = Math.max(0, ranges[i].start);
            var e = ranges[i].end;
            if (s > cursor + 0.01) keep.push([cursor, s]);
            cursor = Math.max(cursor, e);
        }
        if (dur > cursor + 0.01) keep.push([cursor, dur]);
        else if (!dur) keep.push([cursor, 1e9]); // unbounded tail when duration unknown
        if (!keep.length) return null;

        var predicate = keep.map(function(k){
            return "between(t," + k[0].toFixed(3) + "," + k[1].toFixed(3) + ")";
        }).join("+");

        var vf = "select='" + predicate + "',setpts=N/FRAME_RATE/TB";
        var af = "aselect='" + predicate + "',asetpts=N/SR/TB";
        return { vf: vf, af: af, keep: keep, ranges: ranges };
    }

    /* Persist checkbox state across reloads so users who turn this on
       don't have to find it again on their next caption job. */
    function restoreCheckboxState() {
        try {
            var saved = localStorage.getItem(STORAGE_KEY);
            var cb = document.getElementById('ac-magic-cut-video');
            if (cb && saved === '1') cb.checked = true;
        } catch (e) {}
    }

    function bindUi() {
        var cb = document.getElementById('ac-magic-cut-video');
        var chip = document.getElementById('ac-magic-cut-preview');
        if (cb) {
            cb.addEventListener('change', function(){
                try { localStorage.setItem(STORAGE_KEY, cb.checked ? '1' : '0'); } catch (e) {}
                updatePreviewChip();
                if (typeof window.__acReportFeature === 'function') {
                    window.__acReportFeature('magic_cut_toggle', { on: !!cb.checked });
                }
            });
        }
        /* Repaint chip after cleaner runs — pipeline emits an event we
           subscribe to. Falls back to a 500 ms tick during dev. */
        window.addEventListener('ac-cleaner-applied', updatePreviewChip);
        updatePreviewChip();
    }

    function updatePreviewChip() {
        var chip = document.getElementById('ac-magic-cut-preview');
        if (!chip) return;
        var secs = previewSecondsCut();
        var tmpl = (window.__acI18n && window.__acI18n.magic_cut_preview) || '-:secs s from video';
        chip.textContent = tmpl.replace(':secs', secs.toFixed(1));
        // Hide chip when there's nothing to preview — empty chip looks broken.
        chip.hidden = secs <= 0;
    }

    window.__acMagicCut = {
        getCutListFromCleaner: getCutListFromCleaner,
        isActive: isActive,
        previewSecondsCut: previewSecondsCut,
        buildFFmpegSelectFilter: buildFFmpegSelectFilter,
        // Internal — exposed for tests and the pipeline hook.
        _refresh: updatePreviewChip,
    };

    /* Self-installing cleaner interceptor. The pipeline's __acApplyCleanup
       calls __acCleaner.clean(state.words, opts) directly, then mutates
       state.words. We wrap clean() once on load so that EVERY call also
       caches `removedRanges` to `state.lastCleanerRun` for later reads by
       getCutListFromCleaner(). This keeps pipeline.js patch-free for the
       cleaner-output capture step. The wrap is idempotent (`__wrapped` flag)
       so reloading the script in dev doesn't double-wrap. */
    function installCleanerInterceptor() {
        if (!window.__acCleaner || !window.__acCleaner.clean) return false;
        if (window.__acCleaner.__magicCutWrapped) return true;
        var original = window.__acCleaner.clean;
        window.__acCleaner.clean = function(words, opts) {
            var res = original.call(this, words, opts);
            try {
                if (window.__acState) {
                    window.__acState.lastCleanerRun = {
                        removedRanges: (res && res.removedRanges) || [],
                        secsCut: (res && res.secsCut) || 0,
                        removedWords: (res && res.removedWords) || 0,
                        at: Date.now(),
                    };
                }
                // Fire an event so the preview chip + any other listeners
                // refresh without polling.
                try {
                    window.dispatchEvent(new CustomEvent('ac-cleaner-applied', {
                        detail: { ranges: (res && res.removedRanges) || [] },
                    }));
                } catch(e){}
            } catch (e) { /* never let telemetry kill the result */ }
            return res;
        };
        window.__acCleaner.__magicCutWrapped = true;
        return true;
    }

    /* DOM may not be ready when this script loads (defer or not). Bind on
       DOMContentLoaded if it hasn't fired yet, otherwise immediately. */
    function boot() {
        installCleanerInterceptor();
        restoreCheckboxState();
        bindUi();
    }
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', boot);
    } else {
        boot();
    }
    // Defensive — auto-captions-cleaner.js is loaded with `defer` so the
    // global may not exist yet at script-evaluation time. Re-attempt on
    // load and on the next macrotask so we always catch it.
    window.addEventListener('load', installCleanerInterceptor);
    setTimeout(installCleanerInterceptor, 0);
})();
